import { gastroSalesDemoPath } from "../gastro-sales-demo/GastroSalesDemo";
import { casaNativaAdminPath, type CasaNativaAdminSection } from "./gastronomy/reference-restaurant/CasaNativaAdmin";
import { DEMO_CATALOG } from "./catalog";
import type { DemoManifest } from "./types";

export type DemoModuleKey =
  | "home"
  | "customers"
  | "crm"
  | "pipeline"
  | "conversations"
  | "agenda"
  | "sales"
  | "settings"
  | "knowledge"
  | "agent"
  | "services"
  | "reservations"
  | "orders"
  | "inventory"
  | "tasks"
  | "reports";

export type DemoCategory = "industry" | "service" | "solution";

export interface DemoModuleDefinition {
  readonly key: DemoModuleKey;
  readonly label: string;
  readonly description: string;
}

export interface DemoDefinition {
  readonly manifest: DemoManifest;
  readonly category: DemoCategory;
  readonly modules: readonly DemoModuleDefinition[];
}

const MODULES: Readonly<Record<DemoModuleKey, DemoModuleDefinition>> = Object.freeze({
  home: Object.freeze({ key: "home", label: "Inicio", description: "Resumen operativo de la demo." }),
  customers: Object.freeze({ key: "customers", label: "Clientes", description: "Clientes y contexto 360 del negocio." }),
  crm: Object.freeze({ key: "crm", label: "CRM", description: "Seguimiento comercial y contexto de clientes." }),
  pipeline: Object.freeze({ key: "pipeline", label: "Pipeline", description: "Oportunidades y estados comerciales." }),
  conversations: Object.freeze({ key: "conversations", label: "Conversaciones", description: "Conversaciones y atención al cliente." }),
  agenda: Object.freeze({ key: "agenda", label: "Agenda", description: "Disponibilidad, turnos y reservas." }),
  sales: Object.freeze({ key: "sales", label: "Ventas", description: "Ventas, pagos y cobros del negocio." }),
  settings: Object.freeze({ key: "settings", label: "Administración", description: "Datos del negocio, equipo, horarios y conexiones." }),
  knowledge: Object.freeze({ key: "knowledge", label: "Conocimiento", description: "FAQs, documentos y conocimiento aprobado del negocio." }),
  agent: Object.freeze({ key: "agent", label: "Asistente IA", description: "Configuración del asistente y capacidades habilitadas." }),
  services: Object.freeze({ key: "services", label: "Servicios", description: "Catálogo de servicios ofrecidos." }),
  reservations: Object.freeze({ key: "reservations", label: "Reservas", description: "Reservas y disponibilidad operativa." }),
  orders: Object.freeze({ key: "orders", label: "Pedidos", description: "Pedidos y flujo operativo asociado." }),
  inventory: Object.freeze({ key: "inventory", label: "Inventario", description: "Stock y disponibilidad de productos o insumos." }),
  tasks: Object.freeze({ key: "tasks", label: "Tareas", description: "Próximas acciones y seguimiento operativo." }),
  reports: Object.freeze({ key: "reports", label: "Reportes", description: "Indicadores y resultados de la demo." }),
});

const DEFAULT_MODULE_KEYS: readonly DemoModuleKey[] = Object.freeze(["home", "conversations", "knowledge", "agent"]);

const MODULE_KEYS_BY_DEMO: Readonly<Record<string, readonly DemoModuleKey[]>> = Object.freeze({
  "beauty.salons.reference-v2": Object.freeze([
    "home", "customers", "conversations", "agenda", "sales", "services", "knowledge", "agent", "reports", "settings",
  ] as const),
  "gastronomy.generic-draft": Object.freeze([
    "home", "customers", "conversations", "reservations", "orders", "inventory", "knowledge", "agent", "reports",
  ] as const),
  "gastronomy.reference-restaurant-v1": Object.freeze([
    "home", "customers", "reservations", "orders", "inventory", "conversations", "knowledge", "agent", "reports",
  ] as const),
  "hospitality.accommodation.reference-pending": Object.freeze([
    "home", "customers", "reservations", "conversations", "knowledge", "agent", "reports",
  ] as const),
  "home-services.renovation.maison-morel-v1": Object.freeze([
    "home", "crm", "pipeline", "conversations", "tasks", "knowledge", "agent", "reports",
  ] as const),
});

export const DEMO_DEFINITIONS: readonly DemoDefinition[] = Object.freeze(
  DEMO_CATALOG.map((manifest) => Object.freeze({
    manifest,
    category: "industry" as const,
    modules: Object.freeze(
      (MODULE_KEYS_BY_DEMO[manifest.id] ?? DEFAULT_MODULE_KEYS)
        .map((key) => MODULES[key]),
    ),
  })),
);

export function demoDefinitionById(id: string): DemoDefinition | null {
  return DEMO_DEFINITIONS.find((definition) => definition.manifest.id === id) ?? null;
}

export function isDemoModuleKey(value: string): value is DemoModuleKey {
  return value in MODULES;
}

export function demoLaunchPath(
  definition: DemoDefinition,
  organizationSlugs: readonly string[],
): string | null {
  const launch = definition.manifest.launch;
  if (launch.kind === "reference-casa-nativa") return casaNativaAdminPath();
  if (launch.kind === "organization-gastro-alias") {
    const slug = organizationSlugs[0];
    return slug ? gastroSalesDemoPath(slug) : null;
  }
  if (launch.kind === "organization-salons-reference") {
    return organizationSlugs.includes(launch.organizationSlug) ? launch.path : null;
  }
  return null;
}

// Route from catalog modules to an existing, real owner surface.
// Never invent a per-module runtime for a reference that has no implementation.
export function demoModuleLaunchPath(
  definition: DemoDefinition,
  moduleKey: DemoModuleKey,
  organizationSlugs: readonly string[],
): string | null {
  if (definition.manifest.launch.kind === "reference-casa-nativa") {
    const sections: Partial<Record<DemoModuleKey, CasaNativaAdminSection>> = {
      home: "inicio", customers: "clientes", reservations: "reservas",
      orders: "pedidos", inventory: "inventario", reports: "analytics",
    };
    return casaNativaAdminPath(sections[moduleKey] ?? "inicio");
  }
  if (definition.manifest.launch.kind === "organization-gastro-alias") {
    const sections: Partial<Record<DemoModuleKey, "today" | "customers" | "orders" | "operations" | "analytics" | "custom">> = {
      home: "today", customers: "customers", orders: "orders", inventory: "operations",
      reports: "analytics", agent: "custom", knowledge: "custom",
    };
    const slug = organizationSlugs[0];
    return slug ? gastroSalesDemoPath(slug, sections[moduleKey] ?? "today") : null;
  }
  return demoLaunchPath(definition, organizationSlugs);
}
